import { Node } from 'prosemirror-model'

import { schema } from '@showrunner/prose-schemas'

import type { DocumentRanges } from '../act-slug-tracker'

type SceneLookup = DocumentRanges['getSceneAt']

// Only the fields we care about from the Page object
// Not a good candidate for Pick<>, should remain independent
export interface ScopedPage {
  start: number
  end: number
  startBlock: Node
}

export interface SceneContinuationInfo {
  sceneContinuesFromPrevious: boolean
  sceneContinuesToNext: boolean
  sceneNumber?: string
  sceneElementNumber?: string
}

/**
 * Build an array of scene continuation information that mirrors the pagination
 * of the document itself
 */
export function findSceneContinuations(
  pages: ScopedPage[],
  getSceneAt: SceneLookup,
): SceneContinuationInfo[] {
  // Because this is page-oriented, it exposes two bits of information: whether
  // the page has a continuation from the previous page, or whether it continues
  // onto the following page. We might want to only consider the "boundary" case
  // instead of the "two pages" case in the future, but this is easiest for the
  // decoration-building function to work with. At that point we could make
  // `sceneContinuesAcrossPageBoundary` public and do it in the widget
  const sceneContinuations: SceneContinuationInfo[] = []
  let currentScene: Node | undefined
  let scenePageNumber = 0
  for (const [beforePage, page, afterPage] of withNeighbors(pages)) {
    const continuedScene = beforePage
      ? sceneFromPreviousPage(beforePage, page, getSceneAt)
      : undefined

    const sceneContinuesFromPrevious = Boolean(continuedScene)
    let sceneNumber: string | undefined
    if (continuedScene) {
      // If new scene, reset the page number, otherwise increment it
      if (continuedScene !== currentScene) {
        currentScene = continuedScene
        scenePageNumber = 1
      } else {
        scenePageNumber += 1
      }
      if (scenePageNumber > 1) sceneNumber = String(scenePageNumber)
    }
    const sceneContinuesToNext = afterPage
      ? sceneContinuesAcrossPageBoundary(page, afterPage, getSceneAt)
      : false

    sceneContinuations.push({
      sceneContinuesFromPrevious,
      sceneContinuesToNext,
      sceneNumber,
      sceneElementNumber: continuedScene?.attrs.elementNumber ?? undefined,
    })
  }

  return sceneContinuations
}

function* withNeighbors<T>(
  items: readonly T[],
): IterableIterator<
  [previous: T | undefined, current: T, next: T | undefined]
> {
  for (let index = 0; index < items.length; index++) {
    yield [items[index - 1], items[index], items[index + 1]]
  }
}

// Encountering one of these nodes "breaks the spell" and resets the
// continuation counter
function startsSection(page: ScopedPage) {
  return (
    page.startBlock.type === schema.nodes.sceneHeading ||
    page.startBlock.type === schema.nodes.new_act
  )
}

function sceneContinuesAcrossPageBoundary(
  beforePage: ScopedPage,
  afterPage: ScopedPage,
  getSceneAt: SceneLookup,
) {
  return Boolean(sceneFromPreviousPage(beforePage, afterPage, getSceneAt))
}

function sceneFromPreviousPage(
  beforePage: ScopedPage,
  afterPage: ScopedPage,
  getSceneAt: SceneLookup,
) {
  if (startsSection(afterPage)) return undefined

  const beforePageEndScene = getSceneAt(beforePage.end)
  const afterPageStartScene = getSceneAt(afterPage.start)

  return beforePageEndScene && beforePageEndScene === afterPageStartScene
    ? afterPageStartScene
    : undefined
}
